import {
  Calendar,
  Clock,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  X,
  Trophy,
  Award,
  Activity,
  Crown,
  Lock,
  Wallet,
  ExternalLink,
  Zap,
  Star,
  Target,
  Percent,
  Flame,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import useUserData from "../../Hooks/useUserData";
import TeamLogo from "../../Components/TeamLogo";

const PredictionCard = ({ prediction }) => {
  const navigate = useNavigate();
  const { userData, updateUserData } = useUserData();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("success");
  const [animateConfidence, setAnimateConfidence] = useState(false);

  const isVIP = userData?.typeU === "Cuenta Vip";
  const userBalance = userData?.saldo || 0;
  const predictionValue = prediction?.value || 0;
  const hasSufficientBalance = userBalance >= predictionValue;

  // Verificar si este pronóstico ya ha sido desbloqueado por el usuario
  useEffect(() => {
    const unlockedPredictions = userData?.unlockedPredictions || [];
    const isPredictionUnlocked = unlockedPredictions.includes(prediction?.id);
    setIsUnlocked(isPredictionUnlocked);
  }, [userData, prediction]);

  // Un pronóstico está bloqueado si:
  // 1. Tiene la propiedad block = true Y
  // 2. El usuario no es VIP Y
  // 3. El usuario no ha comprado este pronóstico específicamente
  const isBlocked = prediction?.block === true && !isVIP && !isUnlocked;

  // Configuración mejorada para estados
  const statusConfig = {
    ganada: {
      bg: "bg-emerald-600",
      gradient: "from-emerald-500 to-green-600",
      icon: <CheckCircle size={16} className="mr-1" />,
      text: "Ganada",
      ringColor: "ring-emerald-500",
    },
    perdida: {
      bg: "bg-rose-600",
      gradient: "from-rose-500 to-red-600",
      icon: <X size={16} className="mr-1" />,
      text: "Perdida",
      ringColor: "ring-rose-500",
    },
    pendiente: {
      bg: "bg-amber-500",
      gradient: "from-amber-400 to-yellow-500",
      icon: <Clock size={16} className="mr-1" />,
      text: "Pendiente",
      ringColor: "ring-amber-400",
    },
    nula: {
      bg: "bg-slate-500",
      gradient: "from-slate-400 to-gray-500",
      icon: <AlertCircle size={16} className="mr-1" />,
      text: "Nula",
      ringColor: "ring-slate-400",
    },
  };

  const status = statusConfig[prediction?.state] || statusConfig.pendiente;

  // Función para mostrar un mensaje modal
  const showMessageModal = (message, type = "success") => {
    setModalMessage(message);
    setModalType(type);
    setShowModal(true);

    // Auto-cerrar después de 3 segundos si es un mensaje de éxito
    if (type === "success") {
      setTimeout(() => {
        setShowModal(false);
      }, 3000);
    }
  };

  const handlePurchase = async () => {
    // Verificar si ya está comprando
    if (isPurchasing) return;

    // Verificar si ya está desbloqueado
    if (isUnlocked) {
      showMessageModal("Ya has desbloqueado este pronóstico", "error");
      return;
    }

    if (!hasSufficientBalance) {
      showMessageModal("Saldo insuficiente", "error");
      return;
    }

    if (!prediction?.id) {
      showMessageModal("ID de predicción inválido", "error");
      return;
    }

    setIsPurchasing(true);
    setPurchaseError(null);

    try {
      console.log("Iniciando compra del pronóstico:", prediction?.id);

      // Actualizar el saldo del usuario
      const newBalance = userBalance - predictionValue;

      // Crear registro de compra en BetPay
      const betPayEntry = {
        predictionId: prediction.id,
        amount: predictionValue,
        purchaseDate: new Date().toISOString(),
        homeTeam: prediction.homeTeam.name,
        awayTeam: prediction.awayTeam.name,
        prediction: prediction.prediction,
        status: "active",
      };

      // Si no existe el array de predicciones desbloqueadas, lo creamos
      const unlockedPredictions = userData?.unlockedPredictions || [];

      // Añadimos esta predicción a las desbloqueadas del usuario
      // solo si no está ya desbloqueada
      if (!unlockedPredictions.includes(prediction.id)) {
        unlockedPredictions.push(prediction.id);
      }

      // Actualizar datos del usuario - IMPORTANTE: usamos saldo, no balance
      const updatedUserData = {
        ...userData,
        saldo: newBalance, // Actualizar saldo, no balance
        BetPay: userData?.BetPay
          ? [...userData.BetPay, betPayEntry]
          : [betPayEntry],
        unlockedPredictions: unlockedPredictions,
      };

      console.log("Datos actualizados:", updatedUserData);

      // Guardar los cambios
      await updateUserData(updatedUserData);

      // Mostrar mensaje de éxito con el modal
      showMessageModal("¡Pronóstico comprado con éxito!", "success");

      // Actualizar el estado local
      setIsUnlocked(true);

      // Reiniciar el estado después de la compra exitosa
      setIsPurchasing(false);
    } catch (error) {
      console.error("Error al procesar la compra:", error);
      showMessageModal(
        `Error al procesar la compra: ${error.message}`,
        "error"
      );
      setIsPurchasing(false);
    }
  };

  const renderPurchaseButton = () => {
    // Si ya está desbloqueado
    if (isUnlocked) {
      return (
        <button
          className="bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold py-2 px-3 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center cursor-not-allowed text-xs"
          disabled={true}
        >
          <CheckCircle size={14} className="mr-1" />
          <span>Comprado</span>
        </button>
      );
    }

    // Si está procesando la compra
    if (isPurchasing) {
      return (
        <button
          className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold py-2 px-3 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center text-xs"
          disabled={true}
        >
          <span>Procesando...</span>
        </button>
      );
    }

    // Si no tiene saldo suficiente
    if (!hasSufficientBalance) {
      return (
        <button
          className="bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold py-2 px-3 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center cursor-not-allowed text-xs"
          disabled={true}
        >
          <Wallet size={14} className="mr-1" />
          <span>Sin saldo</span>
        </button>
      );
    }

    // Botón activo para comprar
    return (
      <button
        onClick={handlePurchase}
        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold py-2 px-3 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center text-xs"
      >
        <Wallet size={14} className="mr-1" />
        <span>{predictionValue}$</span>
      </button>
    );
  };

  // Botón para casa de apuestas recomendada con efectos
  const handleBetHouseClick = () => {
    // Construir mensaje con información del evento deportivo
    const eventInfo = [
      `🏆 *EVENTO DEPORTIVO VIP* 🏆`,
      ``,
      `⚽ *Partido:* ${prediction?.homeTeam?.name || ""} vs ${prediction?.awayTeam?.name || ""}`,
      `📅 *Fecha:* ${prediction?.date || ""}`,
      `🕐 *Hora:* ${prediction?.hours || ""}`,
      `🎯 *Pronóstico:* ${prediction?.prediction || ""}`,
      `📊 *Confianza:* ${prediction?.confidence || ""}%`,
      `💰 *Cuota:* ${prediction?.odds || ""}`,
      `💎 *Tipo:* ${prediction?.block ? "VIP" : "Gratuito"}`,
      ``,
      `💬 *Mensaje:* Hola, quiero información sobre esta apuesta VIP para el partido mencionado.`
    ].filter(line => line.trim() !== "").join('\n');

    // Abrir WhatsApp con la información del evento
    window.open(
      `https://wa.me/573023580862?text=${encodeURIComponent(eventInfo)}`,
      '_blank'
    );
  };

  // Efecto de pulso para el botón de casa de apuestas y confianza
  const [pulseBetButton, setPulseBetButton] = useState(true);

  // Animar el porcentaje de confianza
  useEffect(() => {
    const animationInterval = setInterval(() => {
      setAnimateConfidence(true);
      setTimeout(() => setAnimateConfidence(false), 2000);
    }, 6000);

    return () => clearInterval(animationInterval);
  }, []);

  // Definir keyframes para animaciones CSS en un estilo global
  useEffect(() => {
    // Solo agregar los estilos una vez
    if (!document.getElementById("prediction-card-styles")) {
      const styleSheet = document.createElement("style");
      styleSheet.id = "prediction-card-styles";
      styleSheet.textContent = `
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes glow {
          0% { box-shadow: 0 0 5px rgba(80, 255, 120, 0.6); }
          50% { box-shadow: 0 0 20px rgba(80, 255, 120, 0.8); }
          100% { box-shadow: 0 0 5px rgba(80, 255, 120, 0.6); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 0.4; }
          100% { transform: scale(0.8); opacity: 0.8; }
        }
        @keyframes confidence-pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        .status-indicator::before {
          content: '';
          position: absolute;
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          border-radius: inherit;
          animation: pulse-ring 2s infinite;
          z-index: -1;
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // Efecto para iniciar la animación del botón de apuestas
  useEffect(() => {
    // Inicia con pulso y luego alterna cada 5 segundos
    const pulseInterval = setInterval(() => {
      setPulseBetButton((prev) => !prev);
    }, 5000);

    return () => clearInterval(pulseInterval);
  }, []);

  // Componente Modal para mensajes
  const Modal = ({ show, type, message, onClose }) => {
    if (!show) return null;

    const bgColor =
      type === "success"
        ? "bg-gradient-to-r from-green-500 to-emerald-600"
        : "bg-gradient-to-r from-red-500 to-rose-600";

    const icon =
      type === "success" ? (
        <CheckCircle size={24} className="text-white" />
      ) : (
        <AlertCircle size={24} className="text-white" />
      );

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
        <div
          className={`${bgColor} rounded-lg p-4 shadow-xl max-w-sm w-full mx-4 transform transition-all duration-300 scale-100`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              {icon}
              <h3 className="ml-2 text-lg font-bold text-white">
                {type === "success" ? "¡Éxito!" : "Error"}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="mt-3">
            <p className="text-white">{message}</p>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={onClose}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1 rounded-md transition-all duration-300"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`m-2 relative overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-xl border border-gray-700 transform transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
        isBlocked ? "ring-1 ring-yellow-500" : ""
      } ${prediction?.state === "ganada" ? "ring-1 ring-green-500" : ""} ${
        prediction?.state === "perdida" ? "ring-1 ring-red-500" : ""
      }`}
    >
      {/* Animación de fondo sutil */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-900 opacity-30"></div>

      {/* Modal para mensajes */}
      <Modal
        show={showModal}
        type={modalType}
        message={modalMessage}
        onClose={() => setShowModal(false)}
      />

      {/* Rediseño de la cabecera con status y badges */}
      <div className="flex justify-between items-center p-2 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
        {/* Lado izquierdo - Status */}
        <div className="flex items-center space-x-2">
          <div
            className={`relative ${status.ringColor} ring-1 rounded-full w-6 h-6 flex items-center justify-center status-indicator`}
          >
            <div
              className={`bg-gradient-to-r ${status.gradient} h-full w-full rounded-full flex items-center justify-center`}
            >
              {status.icon}
            </div>
          </div>
          <span className="text-white text-xs font-medium">{status.text}</span>
        </div>

        {/* Lado derecho - Badges */}
        <div className="flex items-center space-x-2">
          {/* Badge de confianza con pulso */}
          <div
            className={`bg-gradient-to-r from-indigo-600 to-purple-700 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center shadow-lg ${
              animateConfidence
                ? "animate-[confidence-pulse_1s_ease-in-out]"
                : ""
            }`}
          >
            <Target size={12} className="mr-1 text-yellow-300" />
            <span className="font-bold">{prediction?.confidence}%</span>
          </div>

          {/* Badge VIP si aplica */}
          {prediction?.block && (
            <div className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center shadow-lg">
              <Crown size={12} className="mr-1" />
              <span>VIP</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-3">
        {/* Info de fecha y hora más estilizado */}
        <div className="flex justify-center items-center mb-3">
          <div className="bg-gray-800 px-3 py-1 rounded-full border border-gray-700 flex items-center shadow-inner">
            <Calendar size={14} className="mr-1 text-blue-400" />
            <span className="text-gray-300 text-xs mr-2">
              {prediction?.date || "Hoy"}
            </span>
            <div className="w-px h-3 bg-gray-600 mx-2"></div>
            <Clock size={14} className="mr-1 text-blue-400" />
            <span className="text-gray-300 text-xs">{prediction?.hours}</span>
          </div>
        </div>

        {/* Panel de equipos (rediseñado) */}
        <div className="bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 rounded-lg p-3 mb-3 shadow-inner border border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center text-center w-2/5">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full p-1 shadow-lg mb-2 flex items-center justify-center ring-1 ring-gray-600">
                <TeamLogo
                  src={prediction?.homeTeam?.logo}
                  alt={prediction?.homeTeam?.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              </div>
              <span className="text-white font-semibold text-xs truncate w-full">
                {prediction?.homeTeam?.name}
              </span>
            </div>

            <div className="flex flex-col items-center">
              <div className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-full w-8 h-8 flex items-center justify-center shadow-lg border border-gray-600">
                <span className="text-white font-bold text-xs">VS</span>
              </div>
            </div>

            <div className="flex flex-col items-center text-center w-2/5">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full p-1 shadow-lg mb-2 flex items-center justify-center ring-1 ring-gray-600">
                <TeamLogo
                  src={prediction?.awayTeam?.logo}
                  alt={prediction?.awayTeam?.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              </div>
              <span className="text-white font-semibold text-xs truncate w-full">
                {prediction?.awayTeam?.name}
              </span>
            </div>
          </div>
        </div>

        {/* Info y Casa de apuestas recomendada (rediseñado) */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-2 shadow-md flex flex-col items-center justify-center border border-gray-700">
            <div className="flex items-center">
              <TrendingUp size={14} className="text-green-400 mr-1" />
              <span className="text-green-400 font-bold text-xs">
                {prediction?.odds}
              </span>
            </div>
            <span className="text-gray-400 text-xs mt-1">CUOTA</span>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-2 shadow-md flex flex-col items-center justify-center border border-gray-700">
            <div className="flex items-center">
              <Award size={14} className="text-blue-400 mr-1" />
              <span className="text-blue-400 font-semibold text-xs">⚽</span>
            </div>
            <span className="text-gray-400 text-xs mt-1">FÚTBOL</span>
          </div>

          <button
            onClick={handleBetHouseClick}
            className={`relative overflow-hidden bg-gradient-to-r from-orange-600 to-yellow-500 hover:from-red-700 hover:to-rose-800 rounded-lg p-2 shadow-md flex flex-col items-center justify-center transition-all duration-300 border border-red-800 ${
              pulseBetButton ? "animate-pulse" : ""
            }`}
          >
            {/* Efectos de destellos */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -inset-x-full top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-[shimmer_2s_infinite]"></div>
              <div className="absolute -inset-y-full right-0 w-px bg-gradient-to-b from-transparent via-white to-transparent opacity-50 animate-[shimmer_2.5s_infinite]"></div>
              <div className="absolute -inset-x-full bottom-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-[shimmer_2s_infinite]"></div>
              <div className="absolute -inset-y-full left-0 w-px bg-gradient-to-b from-transparent via-white to-transparent opacity-50 animate-[shimmer_2.5s_infinite]"></div>
            </div>

            <div className="flex items-center">
              <Flame size={14} className="text-red-700 mr-1" />
              <span className="text-white font-semibold text-xs">VIP</span>
            </div>
            {/* <span className="text-white text-xs opacity-80 mt-1"></span> */}

            {/* Destello central */}
            <div className="absolute inset-0 bg-white opacity-0 hover:opacity-20 transition-opacity duration-300"></div>
          </button>

        </div>

        {isBlocked ? (
          <>
            <div className="bg-gradient-to-r from-yellow-900 via-amber-800 to-yellow-900 rounded-lg p-3 mb-3 shadow-md border border-yellow-800 relative overflow-hidden">
              <p className="text-yellow-300 font-medium text-center blur-sm select-none text-sm">
                Pronóstico exclusivo para miembros VIP
              </p>
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                <Lock size={24} className="text-yellow-300 animate-pulse" />
              </div>
            </div>

            {/* Precio y botones en horizontal con mejor organización */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-2 flex items-center justify-center border border-gray-700">
                <div className="flex flex-col items-center">
                  <div className="flex items-center">
                    <Wallet size={14} className="text-green-400 mr-1" />
                    <span className="text-green-400 font-medium text-xs">
                      ${predictionValue.toFixed(2)}
                    </span>
                  </div>
                  <span className="text-gray-400 text-xs mt-1">PRECIO</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => navigate("/payment")}
                  className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white font-bold py-2 px-2 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center text-xs"
                >
                  <Crown size={14} className="mr-1" />
                  <span>VIP</span>
                </button>

                {renderPurchaseButton()}
              </div>
            </div>

            {/* Mensaje de error */}
            {purchaseError && (
              <div className="bg-red-900 text-red-200 p-1 rounded-lg text-xs text-center mb-2">
                {purchaseError}
              </div>
            )}
          </>
        ) : (
          <div className="bg-gradient-to-r from-yellow-900 via-amber-800 to-yellow-900 rounded-lg p-3 mb-3 shadow-md border border-yellow-700 relative overflow-hidden">
            {/* Efecto de destello en el pronóstico */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-10 transform -rotate-45 animate-[shimmer_3s_infinite]"></div>
            <p className="text-yellow-300 font-medium text-center text-sm">
              {prediction?.prediction}
            </p>
          </div>
        )}

        {prediction?.state === "ganada" && !isBlocked && (
          <div className="mt-2 bg-gradient-to-r from-green-900 to-emerald-900 rounded-lg p-2 border border-green-700 shadow-md">
            <div className="flex items-center justify-center">
              <Trophy size={14} className="text-green-300 mr-1" />
              <span className="text-green-300 text-xs font-medium">
                Retorno: +{(prediction?.odds * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictionCard;
